import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { apiBaseUrl } from '../../util/api';

// ================ Action types ================ //

export const LOAD_SUBSCRIPTIONS_REQUEST = 'app/ManageSubscriptionsPage/LOAD_SUBSCRIPTIONS_REQUEST';
export const LOAD_SUBSCRIPTIONS_SUCCESS = 'app/ManageSubscriptionsPage/LOAD_SUBSCRIPTIONS_SUCCESS';
export const LOAD_SUBSCRIPTIONS_ERROR = 'app/ManageSubscriptionsPage/LOAD_SUBSCRIPTIONS_ERROR';

export const CANCEL_SUBSCRIPTION_REQUEST = 'app/ManageSubscriptionsPage/CANCEL_SUBSCRIPTION_REQUEST';
export const CANCEL_SUBSCRIPTION_SUCCESS = 'app/ManageSubscriptionsPage/CANCEL_SUBSCRIPTION_SUCCESS';
export const CANCEL_SUBSCRIPTION_ERROR = 'app/ManageSubscriptionsPage/CANCEL_SUBSCRIPTION_ERROR';

export const REACTIVATE_SUBSCRIPTION_REQUEST = 'app/ManageSubscriptionsPage/REACTIVATE_SUBSCRIPTION_REQUEST';
export const REACTIVATE_SUBSCRIPTION_SUCCESS = 'app/ManageSubscriptionsPage/REACTIVATE_SUBSCRIPTION_SUCCESS';
export const REACTIVATE_SUBSCRIPTION_ERROR = 'app/ManageSubscriptionsPage/REACTIVATE_SUBSCRIPTION_ERROR';

// ================ Reducer ================ //

const initialState = {
  subscriptionsGrouped: {},
  subscriptionsFlat: [],
  loadingSubscriptions: false,
  subscriptionsError: null,
  cancellingSubscription: false,
  cancelSubscriptionError: null,
  reactivatingSubscription: false,
  reactivateSubscriptionError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case LOAD_SUBSCRIPTIONS_REQUEST:
      return {
        ...state,
        loadingSubscriptions: true,
        subscriptionsError: null,
      };
    case LOAD_SUBSCRIPTIONS_SUCCESS:
      return {
        ...state,
        loadingSubscriptions: false,
        subscriptionsGrouped: payload.subscriptionsGrouped,
        subscriptionsFlat: payload.subscriptionsFlat,
      };
    case LOAD_SUBSCRIPTIONS_ERROR:
      return {
        ...state,
        loadingSubscriptions: false,
        subscriptionsError: payload,
      };

    case CANCEL_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        cancellingSubscription: true,
        cancelSubscriptionError: null,
      };
    case CANCEL_SUBSCRIPTION_SUCCESS:
      // Remove cancelled subscription from state
      const { listingId } = payload;
      const updatedGrouped = { ...state.subscriptionsGrouped };

      // Find and update the subscription in grouped data
      Object.keys(updatedGrouped).forEach(category => {
        updatedGrouped[category] = updatedGrouped[category].map(sub => {
          if (sub.listingId === listingId) {
            return {
              ...sub,
              subscriptionStatus: 'cancelled',
              isActive: false,
            };
          }
          return sub;
        });
      });

      const updatedFlat = state.subscriptionsFlat.map(sub => {
        if (sub.listingId === listingId) {
          return {
            ...sub,
            subscriptionStatus: 'cancelled',
            isActive: false,
          };
        }
        return sub;
      });

      return {
        ...state,
        cancellingSubscription: false,
        subscriptionsGrouped: updatedGrouped,
        subscriptionsFlat: updatedFlat,
      };
    case CANCEL_SUBSCRIPTION_ERROR:
      return {
        ...state,
        cancellingSubscription: false,
        cancelSubscriptionError: payload,
      };

    case REACTIVATE_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        reactivatingSubscription: true,
        reactivateSubscriptionError: null,
      };
    case REACTIVATE_SUBSCRIPTION_SUCCESS:
      // Update subscription status in state
      const { listingId: reactivatedListingId } = payload;
      const reactivatedGrouped = { ...state.subscriptionsGrouped };

      Object.keys(reactivatedGrouped).forEach(category => {
        reactivatedGrouped[category] = reactivatedGrouped[category].map(sub => {
          if (sub.listingId === reactivatedListingId) {
            return {
              ...sub,
              subscriptionStatus: 'active',
              isActive: true,
            };
          }
          return sub;
        });
      });

      const reactivatedFlat = state.subscriptionsFlat.map(sub => {
        if (sub.listingId === reactivatedListingId) {
          return {
            ...sub,
            subscriptionStatus: 'active',
            isActive: true,
          };
        }
        return sub;
      });

      return {
        ...state,
        reactivatingSubscription: false,
        subscriptionsGrouped: reactivatedGrouped,
        subscriptionsFlat: reactivatedFlat,
      };
    case REACTIVATE_SUBSCRIPTION_ERROR:
      return {
        ...state,
        reactivatingSubscription: false,
        reactivateSubscriptionError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const loadSubscriptionsRequest = () => ({ type: LOAD_SUBSCRIPTIONS_REQUEST });
export const loadSubscriptionsSuccess = (subscriptionsGrouped, subscriptionsFlat) => ({
  type: LOAD_SUBSCRIPTIONS_SUCCESS,
  payload: { subscriptionsGrouped, subscriptionsFlat },
});
export const loadSubscriptionsError = error => ({
  type: LOAD_SUBSCRIPTIONS_ERROR,
  payload: error,
  error: true,
});

export const cancelSubscriptionRequest = () => ({ type: CANCEL_SUBSCRIPTION_REQUEST });
export const cancelSubscriptionSuccess = listingId => ({
  type: CANCEL_SUBSCRIPTION_SUCCESS,
  payload: { listingId },
});
export const cancelSubscriptionError = error => ({
  type: CANCEL_SUBSCRIPTION_ERROR,
  payload: error,
  error: true,
});

export const reactivateSubscriptionRequest = () => ({ type: REACTIVATE_SUBSCRIPTION_REQUEST });
export const reactivateSubscriptionSuccess = listingId => ({
  type: REACTIVATE_SUBSCRIPTION_SUCCESS,
  payload: { listingId },
});
export const reactivateSubscriptionError = error => ({
  type: REACTIVATE_SUBSCRIPTION_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

export const loadUserSubscriptions = userId => (dispatch, getState, sdk) => {
  dispatch(loadSubscriptionsRequest());

  const baseUrl = apiBaseUrl();
  return fetch(`${baseUrl}/api/user-subscriptions?userId=${userId}`)
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to load subscriptions');
      }
      return response.json();
    })
    .then(data => {
      dispatch(
        loadSubscriptionsSuccess(data.subscriptionsGrouped || {}, data.subscriptionsFlat || [])
      );
      return data;
    })
    .catch(e => {
      dispatch(loadSubscriptionsError(storableError(e)));
      throw e;
    });
};

export const cancelSubscription = (listingId, subscriptionId) => (dispatch, getState, sdk) => {
  dispatch(cancelSubscriptionRequest());

  const baseUrl = apiBaseUrl();
  return fetch(`${baseUrl}/api/cancel-listing-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      listingId,
      reason: 'user_closed',
    }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }
      return response.json();
    })
    .then(() => {
      dispatch(cancelSubscriptionSuccess(listingId));

      // Refresh current user data to get updated listings
      return dispatch(fetchCurrentUser());
    })
    .then(() => {
      // Redirect to subscription management page after successful cancellation
      window.location.href = '/manage-subscriptions';
    })
    .catch(e => {
      dispatch(cancelSubscriptionError(storableError(e)));
      throw e;
    });
};

export const reactivateSubscription = (listingId) => (dispatch, getState, sdk) => {
  dispatch(reactivateSubscriptionRequest());

  const baseUrl = apiBaseUrl();
  return fetch(`${baseUrl}/api/reactivate-listing-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      listingId,
    }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to reactivate subscription');
      }
      return response.json();
    })
    .then(data => {
      if (data.success && data.checkoutUrl) {
        // Redirect to Stripe checkout for payment
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('Failed to create checkout session');
      }
    })
    .catch(e => {
      dispatch(reactivateSubscriptionError(storableError(e)));
      throw e;
    });
};

export const loadData = () => (dispatch, getState, sdk) => {
  const state = getState();
  const currentUser = state.user.currentUser;

  if (currentUser && currentUser.id) {
    return dispatch(loadUserSubscriptions(currentUser.id.uuid));
  }

  return Promise.resolve();
};
